/**
 * Login / app bootstrap timing diagnostics.
 *
 * Records each step from page load to the layout being visible, plus every /api request made
 * meanwhile (network timing and the backend Server-Timing breakdown when ServerTiming:Enabled is on).
 *
 * Enabled in development, or anywhere with: localStorage.setItem("loginTiming", "true") and a reload.
 * The report is printed to the console once the layout is visible, kept in window.__loginTiming,
 * and sent to Application Insights as a "LoginTiming" event when it is configured.
 */

const isEnabled = () => {
    try {
        return process.env.NODE_ENV === "development" || localStorage.getItem("loginTiming") === "true";
    } catch {
        return false;
    }
};

const enabled = isEnabled();
const steps = [];
let reported = false;

const round = (value) => Math.round(value * 10) / 10;

const formatServerTiming = (entry) =>
    (entry.serverTiming || [])
        .map(
            (metric) => `${metric.name}${metric.description ? `(${metric.description})` : ""}=${round(metric.duration)}`
        )
        .join(" ");

export const loginTiming = {
    /**
     * Records a point in time (no duration).
     * @param {string} step The step name.
     */
    mark(step) {
        if (!enabled || reported) return;
        steps.push({ step, startMs: round(performance.now()), durationMs: null });
    },

    /**
     * Records a step that started at startTime and ends now (for callback-style code).
     * @param {string} step The step name.
     * @param {number} startTime The performance.now() value when the step started.
     */
    add(step, startTime) {
        if (!enabled || reported) return;
        steps.push({ step, startMs: round(startTime), durationMs: round(performance.now() - startTime) });
    },

    /**
     * Times an async step.
     * @param {string} step The step name.
     * @param {Function} fn The async function to run.
     * @returns {Promise<*>} The function result.
     */
    async time(step, fn) {
        if (!enabled || reported) return fn();

        const start = performance.now();
        try {
            return await fn();
        } finally {
            steps.push({ step, startMs: round(start), durationMs: round(performance.now() - start) });
        }
    },

    /**
     * Prints the report once.
     * @param {object} [appInsights] Application Insights instance to send the timings to.
     */
    report(appInsights) {
        if (!enabled || reported) return;
        reported = true;

        const layoutVisibleMs = round(performance.now());
        const navigation = performance.getEntriesByType("navigation")[0];

        const requests = performance
            .getEntriesByType("resource")
            .filter((entry) => entry.name.includes("/api/"))
            .map((entry) => ({
                request: new URL(entry.name).pathname,
                startMs: round(entry.startTime),
                durationMs: round(entry.duration),
                // time to first byte once the request was sent: backend + network latency
                waitingMs: entry.responseStart > 0 ? round(entry.responseStart - entry.requestStart) : null,
                downloadMs: entry.responseStart > 0 ? round(entry.responseEnd - entry.responseStart) : null,
                server: formatServerTiming(entry),
            }));

        const orderedSteps = [...steps].sort((a, b) => a.startMs - b.startMs);

        console.groupCollapsed(`Login timing: layout visible ${layoutVisibleMs} ms after page load`);
        if (navigation) {
            console.log(
                `Page: HTML received ${round(navigation.responseEnd)} ms, DOMContentLoaded ${round(
                    navigation.domContentLoadedEventEnd
                )} ms, load ${round(navigation.loadEventEnd)} ms`
            );
        }
        console.table(orderedSteps);
        console.table(requests);
        console.groupEnd();

        window.__loginTiming = { layoutVisibleMs, steps: orderedSteps, requests };

        if (appInsights) {
            const measurements = { layoutVisibleMs };
            orderedSteps
                .filter((step) => step.durationMs !== null)
                .forEach((step) => (measurements[step.step] = step.durationMs));
            appInsights.trackEvent({ name: "LoginTiming" }, measurements);
        }
    },
};

export default loginTiming;
